import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Status } from "src/core/constants/folders";
import { CustomRiskLevel } from "src/core/constants/risk";
import { Roles } from "src/core/constants/roles";
import { useFolder } from "src/core/contexts/FolderContext";
import {
  defaultModulerRiskValues,
  ModulerRiskFormValues,
  modulerRiskSchema,
} from "src/core/forms/cartography/modulerRisk.schema";
import { useForceRiskLevelMutation } from "src/core/gql/graphql";
import { useForcedRiskReasonOptions } from "src/core/hooks/common/useForcedRiskReasonOptions";
import { useFolderRisk } from "src/core/hooks/folder/useFolderRisk";
import { toast } from "src/core/hooks/use-toast";
import { usePermissions } from "src/core/hooks/usePermissions";
import { mapCustomRiskLevelToRiskLevel } from "src/core/mappers/risk.mapper";

type LastSubmitted = {
  tab: CustomRiskLevel;
  reason?: string;
};

type Props = {
  open?: boolean;
  setOpen?: (open: boolean) => void;
  isModal?: boolean;
};

export const useModulerRisk = ({ open, setOpen, isModal = true }: Props) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const { folderFile, refetch, setIsModulationInProgress } = useFolder();
  const { hasRole, hasAnyRole } = usePermissions();

  const folderState = folderFile.folderFile?.workflowStatus?.state?.code;
  const isValidation =
    folderState === Status.VALIDATION_PENDING || folderState === Status.VALIDATION_PENDING_HR;
  const isReadOnly =
    !isValidation ||
    hasAnyRole([Roles.AUDITEUR, Roles.COLLABORATEUR]) ||
    (hasRole(Roles.EXPERT_COMPTABLE) && folderState === Status.VALIDATION_PENDING_HR);
  const isHighRisk = folderState === Status.VALIDATION_PENDING_HR;

  const standardRiskLevel = useFolderRisk("standard");
  const globalRiskLevel = useFolderRisk("global");
  const forcedReason = useFolderRisk("forcedReason");

  const [currentTab, setCurrentTab] = useState<CustomRiskLevel>(standardRiskLevel);

  const form = useForm<ModulerRiskFormValues>({
    resolver: zodResolver(modulerRiskSchema(t)),
    mode: "onSubmit",
    defaultValues: defaultModulerRiskValues(forcedReason ?? { code: "", name: "" }),
  });

  const reason = useWatch({
    control: form.control,
    name: "reason",
  });

  /**
   * La "dernière valeur validée" = ce qui est actuellement enregistré côté dossier.
   * On l'initialise avec globalRiskLevel / forcedReason.
   */
  const initialLastSubmitted = useMemo<LastSubmitted>(
    () => ({
      tab: globalRiskLevel,
      reason: forcedReason?.code ?? undefined,
    }),
    [globalRiskLevel, forcedReason?.code]
  );

  const [lastSubmitted, setLastSubmitted] = useState<LastSubmitted>(initialLastSubmitted);

  // Si le dossier change (refetch) et qu'on est revenu au défaut, on resync la base "validée"
  useEffect(() => {
    const isDefaultTab = currentTab === standardRiskLevel;
    if (isDefaultTab) {
      setLastSubmitted(initialLastSubmitted);
    }
  }, [currentTab, standardRiskLevel, initialLastSubmitted]);

  const [forceRiskLevel] = useForceRiskLevelMutation();

  const { forcedRiskReasonOptions, loading: forcedRiskReasonLoading } =
    useForcedRiskReasonOptions();

  const onSubmit = useCallback(
    async (data: ModulerRiskFormValues) => {
      if (!folderFile?.folderFile?.id) {
        return;
      }

      setIsSaving(true);

      try {
        const riskLevel = mapCustomRiskLevelToRiskLevel(currentTab);

        await forceRiskLevel({
          variables: {
            folderFileId: folderFile?.folderFile?.id,
            riskLevel: riskLevel,
            forcedRiskReasonCode: data.reason,
          },
        });

        await refetch();

        // La modulation est maintenant validée
        setLastSubmitted({ tab: currentTab, reason: data.reason });
        setIsModulationInProgress(false);

        toast({
          description: t("form.modulerRisk.success"),
        });
      } catch (error) {
        console.error("useModulerRisk::error", error);
        toast({
          description: t("form.modulerRisk.fail"),
          variant: "destructive",
        });
      } finally {
        setIsSaving(false);
        setOpen?.(false);
      }
    },
    [
      refetch,
      setOpen,
      currentTab,
      forceRiskLevel,
      folderFile?.folderFile?.id,
      t,
      setIsModulationInProgress,
    ]
  );

  useEffect(() => {
    if (open || !isModal) {
      setCurrentTab(globalRiskLevel);
    }
  }, [open, globalRiskLevel, isModal]);

  useEffect(() => {
    if (isReadOnly) {
      setIsModulationInProgress(false);
      return;
    }

    const isDefaultTab = currentTab === standardRiskLevel;
    if (isDefaultTab) {
      setIsModulationInProgress(false);
      return;
    }

    const isSameAsLastSubmitted =
      currentTab === lastSubmitted.tab && (reason ?? undefined) === lastSubmitted.reason;

    // True tant que pas submit (même si reason est déjà sélectionnée)
    setIsModulationInProgress(!isSameAsLastSubmitted);
  }, [
    currentTab,
    standardRiskLevel,
    reason,
    lastSubmitted.tab,
    lastSubmitted.reason,
    isReadOnly,
    setIsModulationInProgress,
  ]);

  return {
    form,
    onSubmit,
    isSaving,
    standardRiskLevel,
    globalRiskLevel,
    forcedRiskReasonOptions,
    loading: forcedRiskReasonLoading,
    isValidation,
    isReadOnly,
    isHighRisk,
    currentTab,
    setCurrentTab,
  };
};
